import {MediaType} from './media';
import {CropProps} from '../screens/TransformationV2/TransformationV2';
import {DEFAULT_IMG_HOST} from '../constants';
import JSZip from 'jszip';
import {toast} from 'react-toastify';

export const GetFileSize = (size: number | undefined) => {
    if (size) {
        const sizeInKB = size / 1024;
        const sizeInMB = sizeInKB / 1024;
        if (sizeInKB < 1000) {
            return `${sizeInKB.toFixed(2)} KB`;
        } else {
            return `${sizeInMB.toFixed(2)} MB`;
        }
    }
    return 'N/A';
};

export const TruncateTitle = (title: string | undefined, maxLength: number): string => {
    if (title && title.length <= maxLength) {
        return title;
    }
    const startLength = Math.floor((maxLength - 3) / 2);
    const endLength = maxLength - startLength - 3;

    return `${title?.substring(0, startLength)}...${title?.substring(title.length - endLength)}`;
};

export const GetRandomPercent = () => {
    const randomDecimal = Math.random();
    const range = 73 - 45 + 1;
    return Math.floor(randomDecimal * range) + 45;
};

export interface PaddingColor {
    padRgb?: string;
    padHex?: string;
}
export interface TransformValues {
    ar?: number;
    orgUid?: string;
    isSvgOrGif?: boolean;
    fileName?: string;
    format?: string;
    quality?: string;
    width?: string;
    height?: string;
    padding?: boolean;
    isBgRemoved?: boolean;
    isGreyToneApplied?: boolean;
    upgradeRequired?: boolean;
    bgColorPalette?: string;
    backgroundImageUid?: string;
    cropProps?: CropProps;
    paddingColor?: PaddingColor;
    rotation?: number;
    blur?: number;
}

function getFileExtension(contentType: string | null): string {
    switch (contentType) {
        case 'image/jpeg':
            return '.jpg';
        case 'image/png':
            return '.png';
        case 'image/gif':
            return '.gif';
        case 'image/webp':
            return '.webp';
        case 'image/svg+xml':
            return '.svg';
        case 'image/svg':
            return '.svg';
        default:
            return '.jpg'; // Default to .jpg if content type is unknown
    }
}

function removeFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    parts.pop();
    return parts.join('');
}

export const DownloadFile = async (imageUrl?: string, fileName?: string) => {
    if (imageUrl && fileName) {
        await fetch(imageUrl, {
            method: 'GET',
            headers: {},
        })
            .then((response) => {
                const contentType = response.headers.get('content-type');
                response.arrayBuffer().then(function (buffer) {
                    const url = window.URL.createObjectURL(new Blob([buffer]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', removeFileExtension(fileName) + getFileExtension(contentType));
                    document.body.appendChild(link);
                    link.click();
                });
            })
            .catch((err) => {
                console.error(err);
            });
    }
};

export const DownloadFilesAsZip = async (imageUrls: string[] = [], fileNames: string[] = []) => {
    if (imageUrls.length !== fileNames.length) {
        console.error('Number of URLs does not match number of filenames');
        return;
    }

    if (imageUrls.length === 0) {
        console.error('No images to download');
        return;
    }

    const zip = new JSZip();

    try {
        await Promise.all(
            imageUrls.map(async (imageUrl, index) => {
                const fileName = fileNames[index];
                if (imageUrl && fileName) {
                    const response = await fetch(imageUrl);
                    const contentType = response.headers.get('content-type');
                    const buffer = await response.arrayBuffer();
                    const modifiedFileName = removeFileExtension(fileName) + getFileExtension(contentType);
                    zip.file(modifiedFileName, buffer, {binary: true});
                }
            }),
        );

        const zipBlob = await zip.generateAsync({type: 'blob'});

        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'perceptpixel_images.zip');
        document.body.appendChild(link);
        link.click();
        toast.success('Download Success');
    } catch (error) {
        toast.error('Something went wrong while downloading zip file. Kindly retry.');
        console.error('Error downloading images as zip:', error);
    }
};

export function generateRandomId(): string {
    const length = 10;
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomId = '';

    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        randomId += characters.charAt(randomIndex);
    }
    return randomId;
}

export function isSvgOrGif(fileName: string | undefined) {
    return (
        fileName?.toLowerCase().endsWith(`.${MediaType.SVG}`) ||
        fileName?.toLowerCase().endsWith(`.${MediaType.SVG_XML}`) ||
        fileName?.toLowerCase().endsWith(`.${MediaType.GIF}`)
    );
}

export function encodeUrl(uri: string): string {
    const lastIndex = uri.lastIndexOf('/');
    const baseUrl = uri.substring(0, lastIndex);
    const lastPathComponent = uri.substring(lastIndex + 1);
    return `${baseUrl}/${encodeURIComponent(lastPathComponent)}`;
}

export const getEffectAppliedImage = (url: string, effect: string) => {
    let modifiedUrlParts = url.split('/');

    let prefixCount = 4;
    if (DEFAULT_IMG_HOST && !url.startsWith(DEFAULT_IMG_HOST)) {
        prefixCount = 3;
    }
    const hostPart = modifiedUrlParts.slice(0, prefixCount);
    const filePath = modifiedUrlParts.slice(prefixCount);
    switch (effect) {
        case 'blur':
            return hostPart.join('/') + '/blur_15_0.5/' + filePath.join('/');
        case 'greyscale':
            return hostPart.join('/') + '/e_grayscale/' + filePath.join('/');
        default:
            return url;
    }
};

export const getCdnUrlParts = (cdnUrl: string) => {
    const parts = cdnUrl.split('/');
    const prefixCount = DEFAULT_IMG_HOST && !cdnUrl.startsWith(DEFAULT_IMG_HOST) ? 3 : 4;
    const hostPart = parts.slice(0, prefixCount).join('/');
    const filePath = parts.slice(prefixCount).join('/');
    return [hostPart, filePath];
};
